<script setup>
import AppLayout from '@/Layouts/AppLayout.vue'
import PageShell from '@/Components/UI/PageShell.vue'
import CustomerChannels from '@/Components/CustomerChannels.vue'
import { useForm } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'

defineOptions({ layout: AppLayout })
const props = defineProps({ driver: { type: Object, required: true } })

const { t } = useI18n()
const isEdit = Boolean(props.driver.id)

const form = useForm({
  name: props.driver.name || '',
  phone: props.driver.phone || '',
  license_number: props.driver.license_number || '',
  license_expiry: props.driver.license_expiry || '',
  notes: props.driver.notes || '',
  photo: null,
  contact_channels: props.driver.contact_channels || [],
  // Create-only: opt-in portal account provisioning in the same
  // request. Controller picks this up when checkbox is ticked and
  // an email is provided.
  invite_portal: false,
  invite_portal_email: '',
})

function submit() {
  const url = isEdit ? `/drivers/${props.driver.id}` : '/drivers'
  const method = isEdit ? 'patch' : 'post'

  form.transform(d => ({
    driver: {
      name: d.name,
      phone: d.phone,
      license_number: d.license_number,
      license_expiry: d.license_expiry,
      notes: d.notes,
      photo: d.photo,
      contact_channels_attributes: d.contact_channels
        .filter(c => c.handle !== '' || c._destroy)
        .map((c, i) => ({ ...c, position: i })),
      // Only send when ticked + email present; create-only.
      invite_portal_email: (!isEdit && d.invite_portal && d.invite_portal_email)
        ? d.invite_portal_email.trim()
        : null,
    },
  }))[method](url, { forceFormData: true })
}
</script>

<template>
  <PageShell
    :title="isEdit ? t('drivers.edit') : t('drivers.new')"
    back-href="/drivers"
  >
    <form
      id="driver-form"
      class="form"
      @submit.prevent="submit"
    >
      <label>
        {{ t('drivers.fields.name') }}
        <input
          v-model="form.name"
          required
        >
      </label>
      <label>
        {{ t('drivers.fields.phone') }}
        <input
          v-model="form.phone"
          type="tel"
        >
      </label>
      <label>
        {{ t('drivers.fields.license_number') }}
        <input v-model="form.license_number">
      </label>
      <label>
        {{ t('drivers.fields.license_expiry') }}
        <input
          v-model="form.license_expiry"
          type="date"
        >
      </label>
      <label>
        {{ t('drivers.fields.photo') }}
        <input
          type="file"
          accept="image/*"
          @change="e => form.photo = e.target.files[0]"
        >
      </label>
      <label class="full">
        {{ t('drivers.fields.notes') }}
        <textarea
          v-model="form.notes"
          rows="3"
        />
      </label>

      <div class="full">
        <h3>{{ t('drivers.channels_heading') }}</h3>
        <CustomerChannels
          v-model="form.contact_channels"
          @update:model-value="v => form.contact_channels = v"
        />
      </div>

      <div v-if="!isEdit" class="full invite-card">
        <label class="invite-toggle">
          <input v-model="form.invite_portal" type="checkbox" />
          <span>{{ t('drivers.invite_portal') }}</span>
        </label>
        <p class="invite-hint">{{ t('drivers.invite_portal_hint') }}</p>
        <div v-if="form.invite_portal" class="invite-email">
          <label>
            {{ t('drivers.invite_portal_email') }}
            <input
              v-model="form.invite_portal_email"
              type="email"
              autocomplete="off"
              :placeholder="t('drivers.invite_portal_email_placeholder')"
              required
            />
          </label>
        </div>
      </div>

      <div
        v-if="Object.keys(form.errors).length"
        class="full errors"
      >
        <ul>
          <li
            v-for="(msg, field) in form.errors"
            :key="field"
          >
            <strong>{{ field }}:</strong> {{ msg }}
          </li>
        </ul>
      </div>

      <button
        type="submit"
        :disabled="form.processing"
        class="submit"
      >
        {{ form.processing ? t('drivers.saving') : (isEdit ? t('drivers.save_changes') : t('drivers.create')) }}
      </button>
    </form>
  </PageShell>
</template>

<style scoped>
.form { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; max-width: 48rem; }
.form label { display: grid; gap: 0.3rem; font-size: 0.875rem; color: var(--inkSoft); }
.form input, .form textarea {
  padding: 0.5rem 0.75rem; background: var(--surface); border: 1px solid var(--line);
  border-radius: var(--radius-input, 8px); color: var(--ink); font: inherit;
}
.full { grid-column: 1 / -1; }
.submit {
  grid-column: 1 / -1; justify-self: start;
  padding: 0.55rem 1.25rem; border: 0; border-radius: var(--radius-button, 8px);
  background: var(--accent); color: var(--accentInk); font-weight: 600; cursor: pointer;
}
.submit:disabled { opacity: 0.5; }
h3 { font-size: 0.875rem; color: var(--muted); margin: 0 0 0.5rem; text-transform: uppercase; }
.errors { background: color-mix(in oklch, var(--danger) 10%, var(--surface)); border: 1px solid color-mix(in oklch, var(--danger) 40%, var(--line)); border-radius: 0.5rem; padding: 0.75rem 1rem; color: var(--danger); }
.errors ul { margin: 0; padding-left: 1.25rem; }
.invite-card {
  background: color-mix(in oklch, var(--accent) 4%, var(--surface));
  border: 1px solid color-mix(in oklch, var(--accent) 25%, var(--line));
  border-radius: 0.5rem; padding: 0.75rem 0.9rem;
}
.invite-toggle { display: flex; align-items: center; gap: 0.5rem; font-weight: 600; color: var(--ink); }
.invite-hint { margin: 0.35rem 0 0; font-size: 0.8125rem; color: var(--muted); }
.invite-email { margin-top: 0.75rem; }
</style>
